import { seoRoutes } from './seo-routes';
import { isDroppedLocale, isEditionIndexable } from './locale-tiers.mjs';

export type LocaleDirection = 'ltr' | 'rtl';

export interface LocaleDefinition {
  code: string;
  pathPrefix: string;
  hreflang: string;
  name: string;
  nativeName: string;
  dir: LocaleDirection;
}

export const defaultLocaleCode = 'en';

export const locales = [
  { code: 'ar', pathPrefix: 'ar', hreflang: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'cs', pathPrefix: 'cs', hreflang: 'cs', name: 'Czech', nativeName: 'Čeština', dir: 'ltr' },
  { code: 'de', pathPrefix: 'de', hreflang: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'el', pathPrefix: 'el', hreflang: 'el', name: 'Greek', nativeName: 'Ελληνικά', dir: 'ltr' },
  { code: 'en', pathPrefix: '', hreflang: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'es', pathPrefix: 'es', hreflang: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'fi', pathPrefix: 'fi', hreflang: 'fi', name: 'Finnish', nativeName: 'Suomi', dir: 'ltr' },
  { code: 'tl', pathPrefix: 'tl', hreflang: 'tl', name: 'Tagalog', nativeName: 'Tagalog', dir: 'ltr' },
  { code: 'fr', pathPrefix: 'fr', hreflang: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'he', pathPrefix: 'he', hreflang: 'he', name: 'Hebrew', nativeName: 'עברית', dir: 'rtl' },
  { code: 'hu', pathPrefix: 'hu', hreflang: 'hu', name: 'Hungarian', nativeName: 'Magyar', dir: 'ltr' },
  { code: 'id', pathPrefix: 'id', hreflang: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', dir: 'ltr' },
  { code: 'it', pathPrefix: 'it', hreflang: 'it', name: 'Italian', nativeName: 'Italiano', dir: 'ltr' },
  { code: 'ja', pathPrefix: 'ja', hreflang: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr' },
  { code: 'ko', pathPrefix: 'ko', hreflang: 'ko', name: 'Korean', nativeName: '한국어', dir: 'ltr' },
  { code: 'ms', pathPrefix: 'ms', hreflang: 'ms', name: 'Malay', nativeName: 'Melayu', dir: 'ltr' },
  { code: 'nl', pathPrefix: 'nl', hreflang: 'nl', name: 'Dutch', nativeName: 'Nederlands', dir: 'ltr' },
  { code: 'pl', pathPrefix: 'pl', hreflang: 'pl', name: 'Polish', nativeName: 'Polski', dir: 'ltr' },
  { code: 'pt', pathPrefix: 'pt', hreflang: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr' },
  { code: 'ro', pathPrefix: 'ro', hreflang: 'ro', name: 'Romanian', nativeName: 'Română', dir: 'ltr' },
  { code: 'ru', pathPrefix: 'ru', hreflang: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr' },
  { code: 'sv', pathPrefix: 'sv', hreflang: 'sv', name: 'Swedish', nativeName: 'Svenska', dir: 'ltr' },
  { code: 'th', pathPrefix: 'th', hreflang: 'th', name: 'Thai', nativeName: 'ไทย', dir: 'ltr' },
  { code: 'tr', pathPrefix: 'tr', hreflang: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr' },
  { code: 'uk', pathPrefix: 'uk', hreflang: 'uk', name: 'Ukrainian', nativeName: 'Українська', dir: 'ltr' },
  { code: 'vi', pathPrefix: 'vi', hreflang: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', dir: 'ltr' },
  { code: 'zh-Hant', pathPrefix: 'zh-hant', hreflang: 'zh-Hant', name: 'Traditional Chinese', nativeName: '繁體中文', dir: 'ltr' },
] as const satisfies readonly LocaleDefinition[];

export type LocaleCode = (typeof locales)[number]['code'];

export const nonDefaultLocales = locales.filter((locale) => locale.code !== defaultLocaleCode);

const localeByCode = new Map(locales.map((locale) => [locale.code.toLowerCase(), locale]));
const localeByPrefix = new Map(
  locales
    .filter((locale) => locale.pathPrefix)
    .map((locale) => [locale.pathPrefix.toLowerCase(), locale]),
);

export const defaultLocale = locales.find((locale) => locale.code === defaultLocaleCode)!;

export const isLocaleCode = (code: string | undefined): code is LocaleCode =>
  Boolean(code && localeByCode.has(code.toLowerCase()));

export const getLocaleByCode = (code: string | undefined) =>
  (code ? localeByCode.get(code.toLowerCase()) : undefined) || defaultLocale;

export const getLocaleFromPath = (path: string | undefined) => {
  const firstSegment = (path || '/').split('/').filter(Boolean)[0]?.toLowerCase();
  return (firstSegment && localeByPrefix.get(firstSegment)) || defaultLocale;
};

export const normalizePath = (path: string | undefined) => {
  if (!path || path === '/') return '/';

  const [pathWithoutHash, hash = ''] = path.split('#');
  const [pathname, query = ''] = pathWithoutHash.split('?');
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  const normalized = query ? `${normalizedPathname}?${query}` : normalizedPathname;

  return hash ? `${normalized}#${hash}` : normalized;
};

export const stripLocaleFromPath = (path: string | undefined) => {
  const normalized = normalizePath(path);
  const [pathWithoutHash, hash = ''] = normalized.split('#');
  const [pathname, query = ''] = pathWithoutHash.split('?');
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] && localeByPrefix.has(segments[0].toLowerCase())) {
    segments.shift();
  }

  const basePath = `/${segments.join('/')}` || '/';
  const withQuery = query ? `${basePath}?${query}` : basePath;

  return hash ? `${withQuery}#${hash}` : withQuery;
};

const withTrailingSlash = (path: string) => {
  const [pathWithoutHash, hash = ''] = path.split('#');
  const [pathname, query = ''] = pathWithoutHash.split('?');
  const pathnameWithSlash =
    pathname === '/' || pathname.endsWith('/') ? pathname : `${pathname}/`;
  const withQuery = query ? `${pathnameWithSlash}?${query}` : pathnameWithSlash;

  return hash ? `${withQuery}#${hash}` : withQuery;
};

export const nativeLocalizedPaths = new Set<string>(['/', '/pricing']);

// The locale whose copy Vue renders for a path. Natively localized paths (/ and
// /pricing) render their own locale. Every other localized path is a generated
// edition: it is prerendered in English and translated afterwards by
// scripts/generate-localized-editions.mjs, so the client renders English too and
// plugins/localized-content.client.ts translates that render before hydration.
// Rendering the real locale there instead makes hydration patch the page.
export const copyLocaleCodeForPath = (path: string | undefined) => {
  const locale = getLocaleFromPath(path);
  if (locale.code === defaultLocaleCode) return defaultLocaleCode;
  const basePath = normalizePath(stripLocaleFromPath(path).split(/[?#]/)[0]);
  return nativeLocalizedPaths.has(basePath) ? locale.code : defaultLocaleCode;
};

export const localizedIndexablePaths = new Set<string>(
  seoRoutes.map((route) => normalizePath(route.path)),
);

export const localizedPath = (path: string | undefined, localeCode: string | undefined) => {
  const locale = getLocaleByCode(localeCode);
  const basePath = stripLocaleFromPath(path);
  const [pathWithoutHash, hash = ''] = basePath.split('#');
  const [pathname, query = ''] = pathWithoutHash.split('?');
  const normalizedPathname = normalizePath(pathname);
  // Links follow the index policy (data/locale-tiers.mjs): a cut edition is still served, but
  // links and the language switcher go to the English page.
  const canLocalizePath =
    locale.code === defaultLocaleCode ||
    (localizedIndexablePaths.has(normalizedPathname) &&
      isEditionIndexable(normalizedPathname, locale.code));
  const localizedPathname =
    locale.code === defaultLocaleCode || !canLocalizePath
      ? normalizedPathname
      : `/${locale.pathPrefix}${normalizedPathname === '/' ? '' : normalizedPathname}`;
  const withQuery = query ? `${localizedPathname}?${query}` : localizedPathname;
  const withHash = hash ? `${withQuery}#${hash}` : withQuery;

  return withTrailingSlash(withHash);
};

// The URL of a locale's edition regardless of the index policy (data/locale-tiers.mjs): cut
// editions are still built and served at this path. Use it to build routes, never for links.
export const editionPath = (path: string, localeCode: string) => {
  const locale = getLocaleByCode(localeCode);
  const normalizedPathname = normalizePath(stripLocaleFromPath(path));
  if (locale.code === defaultLocaleCode) return withTrailingSlash(normalizedPathname);
  return withTrailingSlash(`/${locale.pathPrefix}${normalizedPathname === '/' ? '' : normalizedPathname}`);
};

export const localizedUrl = (
  siteUrl: string,
  path: string | undefined,
  localeCode: string | undefined,
) => {
  const cleanSiteUrl = siteUrl.replace(/\/$/, '');
  const pathForLocale = localizedPath(path, localeCode);

  return `${cleanSiteUrl}${pathForLocale === '/' ? '/' : pathForLocale}`;
};

export const isLocalizedIndexablePath = (
  path: string | undefined,
  localeCode: string | undefined,
) => {
  const locale = getLocaleByCode(localeCode);

  if (locale.code === defaultLocaleCode) return true;

  const basePath = stripLocaleFromPath(path);
  return localizedIndexablePaths.has(basePath) && isEditionIndexable(basePath, locale.code);
};

// The languages offered in the language switcher: dropped languages stay served but unlisted.
export const switcherLocales = locales.filter((locale) => !isDroppedLocale(locale.code));

export const localeUi = {
  openApp: 'Open app',
  signIn: 'Sign in',
  startFree: 'Start free trial',
  menu: 'Menu',
  language: 'Language',
  topBannerFull: '2026 suite live: inbox, voice, AI, automations, accounting, and inventory.',
  topBannerShort: '2026 suite: inbox, AI, accounting.',
  nav: {
    home: 'Home',
    solutions: 'Solutions',
    features: 'Features',
    compare: 'Compare',
    integrations: 'Integrations',
    buildWithAi: 'Build with AI',
    pricing: 'Pricing',
    about: 'About',
    trust: 'Trust',
    updates: 'Updates',
  },
};

export const localizedSeoCopy: Record<string, { title: string; description: string }> = {
  ar: {
    title: 'Workspace369 | نظام تشغيل لأعمال العملاء',
    description: 'أرسل عروضًا بعلامتك التجارية يوقّعها العملاء ويدفعون عربونها أونلاين، ثم أدر العمل: CRM ومشاريع وفواتير ومدفوعات وحجوزات وبريد وذكاء اصطناعي.',
  },
  cs: {
    title: 'Workspace369 | Operační systém pro klientskou práci',
    description: 'Posílejte značkové nabídky, které klienti online podepíší a zaplatí, a pak řiďte práci: CRM, projekty, faktury, platby, rezervace, inbox a AI.',
  },
  de: {
    title: 'Workspace369 | Betriebssystem für Kundenarbeit',
    description: 'Gebrandete Angebote, die Kunden online unterschreiben und bezahlen, und danach die Arbeit: CRM, Projekte, Rechnungen, Zahlungen, Buchungen, Postfach und KI.',
  },
  el: {
    title: 'Workspace369 | Λειτουργικό σύστημα για εργασίες πελατών',
    description: 'Επώνυμες προσφορές που οι πελάτες υπογράφουν και πληρώνουν online και μετά η δουλειά: CRM, έργα, τιμολόγια, πληρωμές, κρατήσεις, inbox και AI.',
  },
  en: {
    title: 'Workspace369 | Client Work Operating System',
    description: 'Send branded proposals clients sign and pay online, then run the work: CRM, projects, invoices, payments, booking, inbox, AI and automations.',
  },
  es: {
    title: 'Workspace369 | Sistema operativo para trabajo con clientes',
    description: 'Propuestas con tu marca que los clientes firman y pagan en línea, y después el trabajo: CRM, proyectos, facturas, pagos, reservas, bandeja de entrada e IA.',
  },
  fi: {
    title: 'Workspace369 | Asiakastyön käyttöjärjestelmä',
    description: 'Brändätyt tarjoukset, jotka asiakas allekirjoittaa ja maksaa verkossa, ja sitten työ: CRM, projektit, laskut, maksut, varaukset, postilaatikko ja tekoäly.',
  },
  tl: {
    title: 'Workspace369 | Operating system para sa client work',
    description: 'Branded na proposal na pinipirmahan at binabayaran ng kliyente online, saka ang trabaho: CRM, proyekto, invoice, bayad, booking, inbox at AI.',
  },
  fr: {
    title: 'Workspace369 | Système d’exploitation pour le travail client',
    description: 'Des propositions à votre image que les clients signent et paient en ligne, puis le travail : CRM, projets, factures, paiements, réservations et IA.',
  },
  he: {
    title: 'Workspace369 | מערכת הפעלה לעבודה מול לקוחות',
    description: 'שלחו הצעות מחיר ממותגות שהלקוחות חותמים ומשלמים עליהן אונליין, ואז נהלו את העבודה: CRM, פרויקטים, חשבוניות, תשלומים, הזמנות, תיבת דואר ו-AI.',
  },
  hu: {
    title: 'Workspace369 | Ügyfélmunka operációs rendszer',
    description: 'Márkázott ajánlatok, amelyeket az ügyfél online aláír és kifizet, utána a munka: CRM, projektek, számlák, fizetések, foglalások, postafiók és AI.',
  },
  id: {
    title: 'Workspace369 | Sistem operasi untuk pekerjaan klien',
    description: 'Kirim proposal bermerek yang ditandatangani dan dibayar klien online, lalu jalankan kerjanya: CRM, proyek, invoice, pembayaran, booking, inbox, dan AI.',
  },
  it: {
    title: 'Workspace369 | Sistema operativo per il lavoro con i clienti',
    description: 'Proposte con il tuo brand che i clienti firmano e pagano online, poi il lavoro: CRM, progetti, fatture, pagamenti, prenotazioni, inbox, AI e automazioni.',
  },
  ja: {
    title: 'Workspace369 | クライアント業務のオペレーティングシステム',
    description: 'ブランドを反映した提案書を送り、クライアントはオンラインで承認・署名し、デポジットを支払えます。その後の業務もCRM、プロジェクト、請求書、支払い、予約、受信箱、AI、自動化で1つのワークスペースから管理できます。',
  },
  ko: {
    title: 'Workspace369 | 고객 업무 운영 시스템',
    description: '브랜드가 반영된 제안서를 보내면 고객이 온라인으로 서명하고 선금을 결제합니다. 이후의 업무는 CRM, 프로젝트, 인보이스, 결제, 예약, 받은편지함, AI와 자동화로 하나의 워크스페이스에서 관리하세요.',
  },
  ms: {
    title: 'Workspace369 | Sistem operasi untuk kerja pelanggan',
    description: 'Hantar cadangan berjenama yang ditandatangani dan dibayar pelanggan dalam talian, kemudian urus kerja: CRM, projek, invois, bayaran, tempahan dan AI.',
  },
  nl: {
    title: 'Workspace369 | Besturingssysteem voor klantwerk',
    description: 'Offertes in je eigen huisstijl die klanten online ondertekenen en betalen, daarna het werk: CRM, projecten, facturen, betalingen, boekingen, inbox en AI.',
  },
  pl: {
    title: 'Workspace369 | System operacyjny do pracy z klientami',
    description: 'Wysyłaj oferty z Twoją marką, które klient podpisuje i opłaca online, a potem prowadź pracę: CRM, projekty, faktury, płatności, rezerwacje i AI.',
  },
  pt: {
    title: 'Workspace369 | Sistema para trabalho com clientes',
    description: 'Propostas com a sua marca que os clientes assinam e pagam online e, depois, o trabalho: CRM, projetos, faturas, pagamentos, agendamentos e IA.',
  },
  ro: {
    title: 'Workspace369 | Sistem de operare pentru munca cu clienții',
    description: 'Oferte cu brandul tău pe care clienții le semnează și le plătesc online, apoi munca: CRM, proiecte, facturi, plăți, rezervări, inbox, AI și automatizări.',
  },
  ru: {
    title: 'Workspace369 | Операционная система для работы с клиентами',
    description: 'Брендированные предложения, которые клиенты подписывают и оплачивают онлайн, а затем работа: CRM, проекты, счета, платежи, бронирования, входящие и AI.',
  },
  sv: {
    title: 'Workspace369 | Operativsystem för kundarbete',
    description: 'Offerter med ditt varumärke som kunden signerar och betalar online, och sedan arbetet: CRM, projekt, fakturor, betalningar, bokningar, inkorg och AI.',
  },
  th: {
    title: 'Workspace369 | ระบบปฏิบัติการสำหรับงานลูกค้า',
    description: 'ส่งข้อเสนอที่มีแบรนด์ของคุณให้ลูกค้าลงนามและชำระมัดจำออนไลน์ แล้วบริหารงานต่อด้วย CRM โปรเจกต์ ใบแจ้งหนี้ การชำระเงิน การจอง และ AI ในที่เดียว',
  },
  tr: {
    title: 'Workspace369 | Müşteri işleri için işletim sistemi',
    description: 'Müşterilerin çevrimiçi imzalayıp ödediği markalı teklifler, ardından iş: CRM, projeler, faturalar, ödemeler, rezervasyonlar, gelen kutusu ve yapay zeka.',
  },
  uk: {
    title: 'Workspace369 | Операційна система для роботи з клієнтами',
    description: 'Брендовані пропозиції, які клієнти підписують і оплачують онлайн, а далі робота: CRM, проєкти, рахунки, платежі, бронювання, вхідні, AI та автоматизації.',
  },
  vi: {
    title: 'Workspace369 | Hệ điều hành cho công việc khách hàng',
    description: 'Gửi đề xuất mang thương hiệu để khách ký và thanh toán trực tuyến, rồi vận hành công việc: CRM, dự án, hóa đơn, thanh toán, đặt lịch, hộp thư và AI.',
  },
  'zh-Hant': {
    title: 'Workspace369 | 客戶工作營運系統',
    description: '發送帶有您品牌的提案，客戶線上簽署並支付訂金，之後在同一個工作空間管理 CRM、專案、發票、付款、預約、收件匣、AI 與自動化。',
  },
};
